import { Globe, RotateCcw, ShieldAlert, UserRound } from 'lucide-react';
import { Input, Label, Textarea, adjustForContrast, cn, getContrastRatio } from '@respectly/ui';
import type {
  ChatBrandDto,
  ChatThemeContrastPair,
  ChatThemeControlKey,
  ChatThemeGroup,
} from '@respectly/shared-types';
import {
  CHAT_THEME_COLOR_PATTERN,
  CHAT_THEME_CONTRAST_PAIRS,
  CHAT_THEME_CONTROLS,
  CHAT_THEME_DEFAULTS,
  CHAT_THEME_GROUPS,
  CHAT_WIDGET_POSITIONS,
  expandChatTheme,
} from '@respectly/shared-types';
import type { ChatBrandDraft } from '../../stores/useChatWidgetsStore';
import { StringListEditor } from './StringListEditor';

/**
 * Defaults are not kept here.
 *
 * This file carried its own copy of the palette, and so did `WidgetPreview`, and
 * both had gone stale against the widget's rework: they still advertised the
 * retired `#A4C7C9` mint and `#FACC15` yellow the widget deliberately dropped.
 * An unset swatch showed a colour the widget never renders, and nudging it wrote
 * that retired colour into the database. `CHAT_THEME_DEFAULTS` is the one copy,
 * checked against the widget's own `BASE_STYLE` by `theme.test.ts`.
 */

/** `<input type="color">` only speaks `#RRGGBB`; anything else it silently rounds. */
const HEX = /^#[0-9a-fA-F]{6}$/;

const CONTROL_KEYS = new Set<string>(CHAT_THEME_CONTROLS.map((t) => t.key));

/** A stored theme reduced to the keys this editor and the API both know. */
function knownControls(theme: Record<string, string>): Record<string, string> {
  return Object.fromEntries(Object.entries(theme).filter(([key]) => CONTROL_KEYS.has(key)));
}

/**
 * What the visitor sees: greeting, starter questions, colours, and where the
 * widget may run.
 */
export function PresentationSection({
  brand,
  draft,
  edit,
}: {
  brand: ChatBrandDto;
  draft: ChatBrandDraft;
  edit: (patch: ChatBrandDraft) => void;
}) {
  const greeting = draft.greeting ?? brand.greeting ?? '';
  const samplePrompts = draft.samplePrompts ?? brand.samplePrompts;
  const quickReplies = draft.quickReplies ?? brand.quickReplies;
  const theme = draft.theme ?? brand.theme;
  const position = draft.position ?? brand.position;
  const allowedOrigins = draft.allowedOrigins ?? brand.allowedOrigins;
  const displayName = draft.displayName ?? brand.displayName;

  /**
   * Write one colour, or drop the key entirely when the value is cleared.
   *
   * The patch is rebuilt from the known keys rather than spread from what was
   * loaded: the API rejects a key it does not recognise, and a row carrying a
   * stray one — an old `hairlineColor`, say, from before the palette was
   * simplified — would otherwise round-trip and make every save fail on
   * something the admin cannot see or reach.
   */
  const setColor = (key: ChatThemeControlKey, value: string) => {
    const next = knownControls(theme);
    const trimmed = value.trim();
    if (trimmed) next[key] = trimmed;
    else delete next[key];
    edit({ theme: next });
  };

  /**
   * Values the widget would drop on arrival.
   *
   * The API rejects these, so this is what stops a save from failing on a
   * character the admin has no way of knowing about.
   */
  const invalid = new Set(
    Object.entries(theme)
      .filter(([, value]) => value && !CHAT_THEME_COLOR_PATTERN.test(value))
      .map(([key]) => key)
  );

  /**
   * The colours as chosen, and the palette the widget will actually be handed.
   *
   * Both are needed. The four checked pairs are measured against what the admin
   * picked, because those are the values they can move. The specimens are drawn
   * from the derived palette, because a reply bubble is `surfaceColor` and a
   * hairline is `hairlineColor` and neither is something anyone chose — showing
   * the picked colours there would make the preview a diagram of the form rather
   * than a picture of the widget.
   */
  const chosen = { ...CHAT_THEME_DEFAULTS, ...theme } as Record<ChatThemeControlKey, string>;
  const derived = expandChatTheme(chosen) as Record<string, string>;

  /**
   * Every checked pairing the current palette fails, filed under the region it
   * belongs to.
   *
   * Only actual failures. "The other choice would score higher" is a different
   * claim from "this is unreadable", and warning on the first nags about pairs
   * that are perfectly fine.
   */
  const failures = CHAT_THEME_CONTRAST_PAIRS.map((pair) => ({
    pair,
    ratio: getContrastRatio(chosen[pair.fg], chosen[pair.bg]),
  }))
    .filter((f): f is { pair: ChatThemeContrastPair; ratio: number } => f.ratio !== null)
    .filter((f) => f.ratio < f.pair.min);

  const touched = Object.keys(knownControls(theme)).length > 0;

  return (
    <div className="space-y-8">
      <Field
        label="Greeting"
        hint="The first thing in the panel. Set expectations here — what it knows, and that it will say so when it doesn't."
      >
        <Textarea
          value={greeting}
          rows={3}
          maxLength={2000}
          onChange={(e) => edit({ greeting: e.target.value || null })}
          placeholder="Ask anything about Respectly — sending, consent, signing, or pricing."
        />
      </Field>

      <Field
        label="Sample prompts"
        hint="The starter questions the empty state offers, in order. Per widget, because the right opening question on a pricing page is not the one on a support page."
      >
        <StringListEditor
          value={samplePrompts}
          onChange={(samplePrompts) => edit({ samplePrompts })}
          placeholder="Do I need consent to text my customers?"
          max={8}
          maxLength={200}
          emptyHint="No sample prompts. The empty state will show the greeting alone."
        />
      </Field>

      <Field
        label="Quick replies"
        hint="Chips offered during the conversation, after the assistant has answered."
      >
        <StringListEditor
          value={quickReplies}
          onChange={(quickReplies) => edit({ quickReplies })}
          placeholder="Talk to a person"
          max={6}
          maxLength={60}
          emptyHint="No quick replies configured."
        />
      </Field>

      <Field label="Position" hint="Which corner the launcher sits in on the host page.">
        <div className="flex gap-2">
          {CHAT_WIDGET_POSITIONS.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => edit({ position: option })}
              className={cn(
                'rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors',
                position === option
                  ? 'border-sky-500 bg-sky-50 text-sky-700 dark:border-sky-700 dark:bg-sky-950/40 dark:text-sky-300'
                  : 'border-slate-200 text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800'
              )}
            >
              {option === 'bottom-right' ? 'Bottom right' : 'Bottom left'}
            </button>
          ))}
        </div>
      </Field>

      <Field
        label="Theme"
        hint="Six colours: a background and its text, for each part of the panel. Everything else — bubble fills, hairlines, hover states, the error and handoff colours — is worked out from them."
      >
        {/*
          Three cards, in the order the eye travels the panel. This replaced a
          grid of eleven swatches named after the widget's own CSS custom
          properties — Ground, Surface, Hairline, Quiet, Ink, Soft ink, Live,
          Handoff, Danger — grouped under four headings that meant nothing to
          anyone who had not read the stylesheet. It asked an admin to know that
          Quiet is a background, that Handoff is only ever text, and that Live and
          Primary are the same teal in every palette anyone actually saved.

          Each card leads with the thing it colours, drawn in the colours as
          chosen, so the pair is legible before any warning fires — and so the
          abstract name stops being load-bearing. You are not choosing "Ground";
          you are choosing what the conversation sits on, and you can see it.
        */}
        <div className="space-y-3">
          {CHAT_THEME_GROUPS.map((group) => (
            <ThemeRegionCard
              key={group.key}
              group={group}
              chosen={chosen}
              derived={derived}
              displayName={displayName}
              greeting={greeting}
              invalid={invalid}
              failures={failures.filter(
                (f) => controlGroup(f.pair.fg) === group.key
              )}
              value={(key) => theme[key] ?? ''}
              onChange={setColor}
            />
          ))}
        </div>

        <div className="mt-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <p className="text-[11px] leading-relaxed text-slate-500 dark:text-slate-400">
            {touched
              ? 'These colours are what every visitor sees.'
              : 'Untouched, so the widget follows each visitor’s system dark mode.'}
          </p>
          {touched && (
            <button
              type="button"
              onClick={() => edit({ theme: {} })}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-500 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
            >
              <RotateCcw className="h-3 w-3" />
              Reset to defaults
            </button>
          )}
        </div>
      </Field>

      <Field label="Allowed origins" hint="The exact hostnames permitted to embed this widget.">
        <StringListEditor
          value={allowedOrigins}
          onChange={(allowedOrigins) => edit({ allowedOrigins })}
          placeholder="respectly.ai"
          addLabel="Add host"
          max={20}
          maxLength={255}
          monospace
          emptyHint="No origins configured — this widget will refuse every cross-origin embed."
        />
        <div className="mt-2 flex gap-2 rounded-lg bg-slate-50 px-3 py-2 dark:bg-slate-800/60">
          <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
          <p className="text-[11px] leading-relaxed text-slate-500 dark:text-slate-400">
            Matched by <strong>exact host</strong>. Add{' '}
            <span className="font-mono">respectly.ai</span> and{' '}
            <span className="font-mono">www.respectly.ai</span> separately — one does not imply the
            other. Full URLs are accepted and reduced to the host on save.
          </p>
        </div>
        {allowedOrigins.length > 0 && (
          <p className="mt-2 flex items-center gap-1.5 text-[11px] text-slate-400">
            <Globe className="h-3 w-3" />
            Live on {allowedOrigins.join(', ')}
          </p>
        )}
      </Field>
    </div>
  );
}

/** Which region a colour belongs to. A pair is filed under its foreground's. */
function controlGroup(key: ChatThemeControlKey): ChatThemeGroup {
  return CHAT_THEME_CONTROLS.find((c) => c.key === key)!.group;
}

type Failure = { pair: ChatThemeContrastPair; ratio: number };

/**
 * One region of the panel: what it looks like, then the two colours that make it.
 *
 * The specimen is the point. An admin picking from a grid of swatches has no way
 * to know that a 4.2:1 status line is the one they just made unreadable, and the
 * old form's answer was to write them a sentence about it. A picture of the thing
 * answers first, and the sentence only has to handle the case where the picture
 * looked fine and the maths disagreed.
 */
function ThemeRegionCard({
  group,
  chosen,
  derived,
  displayName,
  greeting,
  invalid,
  failures,
  value,
  onChange,
}: {
  group: (typeof CHAT_THEME_GROUPS)[number];
  chosen: Record<ChatThemeControlKey, string>;
  derived: Record<string, string>;
  displayName: string;
  greeting: string;
  invalid: Set<string>;
  failures: Failure[];
  value: (key: ChatThemeControlKey) => string;
  onChange: (key: ChatThemeControlKey, next: string) => void;
}) {
  const controls = CHAT_THEME_CONTROLS.filter((c) => c.group === group.key);

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
      <Specimen
        group={group.key}
        chosen={chosen}
        derived={derived}
        displayName={displayName}
        greeting={greeting}
      />

      <div className="grid gap-px bg-slate-200 sm:grid-cols-2 dark:bg-slate-800">
        {controls.map((control) => (
          <SwatchField
            key={control.key}
            control={control}
            value={value(control.key)}
            error={invalid.has(control.key) ? 'Not a colour the widget can render.' : undefined}
            onChange={(next) => onChange(control.key, next)}
          />
        ))}
      </div>

      {failures.length > 0 && (
        <div className="space-y-1 border-t border-slate-200 bg-amber-50/60 px-3 py-2 dark:border-slate-800 dark:bg-amber-950/20">
          {failures.map((failure) => (
            <ContrastWarning
              key={`${failure.pair.fg}-${failure.pair.bg}`}
              failure={failure}
              palette={chosen}
              onFix={onChange}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * The region, drawn in the colours as chosen.
 *
 * Each of the three is a real fragment of the panel — the rail with its presence
 * dot, a greeting above a reply bubble, a link beside the Send button — anchored
 * to the rules in `apps/chat-widget/src/panel/styles.ts` that draw them. Small
 * enough to sit above two form fields, specific enough that the colour underneath
 * it does not need a name.
 */
function Specimen({
  group,
  chosen,
  derived,
  displayName,
  greeting,
}: {
  group: ChatThemeGroup;
  chosen: Record<ChatThemeControlKey, string>;
  derived: Record<string, string>;
  displayName: string;
  greeting: string;
}) {
  if (group === 'header') {
    return (
      <div
        className="flex items-center gap-2.5 px-3.5 py-3"
        style={{ background: chosen.headerColor }}
      >
        <span
          aria-hidden
          className="grid h-8 w-8 flex-none place-items-center rounded-full text-[11px] font-semibold"
          style={{ background: chosen.headerInkColor, color: chosen.headerColor }}
        >
          {displayName.slice(0, 2).toUpperCase()}
        </span>
        <div className="min-w-0 flex-1">
          <p
            className="truncate text-[13px] font-semibold leading-tight"
            style={{ color: chosen.headerInkColor }}
          >
            {displayName}
          </p>
          <p
            className="mt-px flex items-center gap-1.5 text-[11px]"
            style={{ color: derived.onHeaderSoftColor }}
          >
            <span
              className="inline-block h-1.5 w-1.5 flex-none rounded-full"
              style={{ background: chosen.headerInkColor }}
            />
            Online now
          </p>
        </div>
        <span
          aria-hidden
          className="text-base leading-none"
          style={{ color: derived.onHeaderSoftColor }}
        >
          ✕
        </span>
      </div>
    );
  }

  if (group === 'body') {
    return (
      <div className="space-y-2 px-3.5 py-3" style={{ background: chosen.bodyColor }}>
        <p
          className="text-[13px] font-semibold leading-snug"
          style={{ color: chosen.bodyInkColor }}
        >
          {greeting || 'Ask me anything.'}
        </p>
        <div
          className="max-w-[92%] rounded-xl rounded-bl-sm px-3 py-2"
          style={{
            background: derived.surfaceColor,
            border: `1px solid ${derived.hairlineColor}`,
          }}
        >
          <p className="text-[12px] leading-relaxed" style={{ color: chosen.bodyInkColor }}>
            Yes — marketing texts need express written consent.
          </p>
          <p className="mt-1 text-[11px]" style={{ color: derived.softInkColor }}>
            Based on your documentation
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex flex-wrap items-center gap-x-4 gap-y-2 px-3.5 py-3"
      style={{ background: chosen.bodyColor }}
    >
      <span
        className="inline-flex items-center gap-1.5 text-[12px] font-semibold underline underline-offset-2"
        style={{ color: chosen.buttonColor }}
      >
        <UserRound className="h-3.5 w-3.5" />
        Talk to a person
      </span>
      <span
        className="rounded-full px-3.5 py-1.5 text-[12px] font-semibold"
        style={{ background: chosen.buttonColor, color: chosen.buttonInkColor }}
      >
        Send
      </span>
    </div>
  );
}

/**
 * One colour: a swatch you can click, and the hex it resolved to.
 *
 * The band is the picker — a transparent `<input type="color">` with the colour
 * painted behind it — so the native swatch popup anchors under what was clicked
 * rather than at a hidden corner. The inset shadow is what keeps a `#FFFFFF`
 * choice visible against a white card.
 *
 * The hex field sits in a wrapper this component owns, and that is deliberate:
 * `@respectly/ui`'s `Input` forwards `className` to its inner `<input>`, not to
 * the `w-full` `<div>` it wraps it in. An earlier version put `w-24 shrink-0` on
 * the `Input` itself, which did nothing — the wrapper claimed the whole flex row,
 * the label was crushed to two characters ("Primary" → "Pr") and the field
 * overflowed into the next column.
 */
function SwatchField({
  control,
  value,
  error,
  onChange,
}: {
  control: (typeof CHAT_THEME_CONTROLS)[number];
  value: string;
  error?: string;
  onChange: (next: string) => void;
}) {
  const isSet = value.length > 0;
  const effective = isSet && HEX.test(value) ? value : control.default;

  return (
    <div className="flex items-start gap-3 bg-white p-3 dark:bg-slate-900">
      <div className="relative h-9 w-9 flex-none">
        <input
          type="color"
          value={effective}
          aria-label={`${control.label} colour`}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          className="peer absolute inset-0 h-full w-full cursor-pointer opacity-0"
        />
        <span
          aria-hidden
          style={{ background: effective }}
          className="pointer-events-none absolute inset-0 rounded-lg shadow-[inset_0_0_0_1px_rgba(0,0,0,0.10)] peer-focus-visible:ring-2 peer-focus-visible:ring-sky-500 peer-focus-visible:ring-offset-1"
        />
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-xs font-semibold text-slate-800 dark:text-slate-100">
            {control.label}
          </span>
          {!isSet && (
            <span className="text-[10px] uppercase tracking-wider text-slate-400">Default</span>
          )}
        </div>
        <p className="mt-0.5 truncate text-[11px] text-slate-500 dark:text-slate-400">
          {control.hint}
        </p>
        <div className="mt-1.5">
          <Input
            value={value}
            placeholder={control.default}
            error={error}
            aria-label={`${control.label} hex value`}
            onChange={(e) => onChange(e.target.value)}
            className="h-8 font-mono text-xs uppercase"
          />
        </div>
      </div>
    </div>
  );
}

/**
 * One failing pairing, under the region it happens in.
 *
 * The fix always moves the *foreground* — darkening the text rather than
 * repainting the panel behind it. "Fix your invisible link by turning the whole
 * conversation grey" is a technically valid, useless answer, and the foreground
 * is also the colour an admin is least attached to: a brand owns its teal, not
 * the exact white it writes on it.
 *
 * `adjustForContrast` preserves hue, so the suggestion is the admin's own colour
 * moved just far enough rather than a neutral substituted for it. It returns null
 * when neither direction can reach the target — a mid-grey has no compliant
 * variant — and the sentence then stands without a button.
 */
function ContrastWarning({
  failure,
  palette,
  onFix,
}: {
  failure: Failure;
  palette: Record<ChatThemeControlKey, string>;
  onFix: (key: ChatThemeControlKey, next: string) => void;
}) {
  const { pair, ratio } = failure;
  const fix = adjustForContrast(palette[pair.fg], palette[pair.bg], pair.min)?.toUpperCase() ?? null;

  return (
    <p className="flex flex-wrap items-baseline gap-x-1.5 text-[11px] leading-snug text-amber-700 dark:text-amber-500">
      <span>
        {pair.where}: {ratio.toFixed(1)}:1, needs {pair.min}.
      </span>
      {fix && (
        <button
          type="button"
          onClick={() => onFix(pair.fg, fix)}
          className="font-medium underline underline-offset-2 hover:no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-500"
        >
          Use {fix}
        </button>
      )}
    </p>
  );
}

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <div>
        <Label className="text-sm font-semibold text-slate-900 dark:text-slate-100">{label}</Label>
        {hint && <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{hint}</p>}
      </div>
      {children}
    </div>
  );
}
